/**
 * Tender helpers — eligibility, scoring, narrative payloads.
 *
 * 01/06/2026 — New AO subsystem.
 * 05/02/2026 (Q1 Lot 2) — Refonte AO :
 *   • L'engagement RSE n'est plus un choix utilisateur (3 boutons HSE)
 *     mais un **SCORE AUTOMATIQUE** calculé depuis le state du joueur.
 *     Cf. `computeAoRseCommitmentScore` ci-dessous.
 *   • La probabilité de victoire est exposée comme une **FOURCHETTE**
 *     `{ low, mid, high }` dont l'amplitude se rétrécit en fonction des
 *     rôles support recrutés (Commerce / Marketing / RH).
 *     Cf. `computeTenderProbabilityRange`.
 *
 * Eligibility filter (a tender appears on the player's board only when ALL
 * these are satisfied) :
 *   1. Player has at least one commercial role hired (Léa / Marc / Sylvie)
 *   2. Player has Claire Maréchal hired (HSE policy is required by buyers)
 *   3. tender.tier rank <= player's commercial level rank
 *   4. vehicles.length >= tender.requiresFleet
 *   5. EcoVadis tier rank >= tender.requiresRseTier rank
 *   6. Medical compliance % >= tender.requiresMedicalPct
 *
 * Probability scoring (computed once the user submits an offer) :
 *   Base 0.50 + sum of factor deltas, clamped 0.05..0.95.
 *
 *   • Reputation/notoriety bundle (max ±0.18)
 *   • Financial offer competitiveness (±0.20)
 *      - offer at 80 % of baseDailyRevenue : +0.20
 *      - offer at 100 % :                    0.00
 *      - offer at 130 % :                   -0.20
 *   • HSE/RSE policy (max +0.15) — based on RSE score & ecoVadis tier
 *   • Fleet over-capacity (max +0.08) — fleet > 2× required
 *   • Financial reliability (max ±0.10) — solvency ratio
 *   • RSE auto-commitment score (computed from state, max +0.15) — replaces
 *     the legacy manual HSE 3-button selector.
 */

import { TENDERS, TENDER_TIER_RANK, getPlayerCommercialLevel, getMaxAccessibleTier } from '../../data/tenders';
import { SPOT_TENDERS } from '../../data/spotTenders';
import { EXCEPTIONAL_TENDERS, isPlayerEligibleForExceptionalTender } from '../../data/exceptionalTenders';
import { getCompanyHseCompliance } from './hseCompliance';
import { computeRseScore } from './rseScore';

const RSE_TIER_RANK = { none: 0, bronze: 1, silver: 2, gold: 3 };

/**
 * Returns the EcoVadis tier earned in the LAST audit (or 'none').
 */
function getPlayerRseTier(state) {
  const last = (state.ecoVadisHistory || []).slice(-1)[0];
  return last ? last.tier : 'none';
}

/**
 * 05/02/2026 (Q1 Lot 2) — Score d'engagement RSE automatique pour les AO.
 *
 * Le joueur n'a plus à choisir « faible / modéré / fort » dans la modale :
 * le score est dérivé de son state (investissements RSE + Claire Maréchal
 * recrutée). Total : 0 → 15 points (mappés en bonus +0.00 → +0.15 sur la
 * probabilité finale dans `computeTenderWinProbability`).
 *
 * Composantes :
 *   • Claire Maréchal recrutée            : +5 pts (politique RSE/HSE en interne)
 *   • Investissement Borne recharge VE actif (`inv-depot-11`) : +3 pts
 *   • Au moins 1 véhicule électrique en flotte                : +3 pts
 *   • EcoVadis : tier silver  → +2 pts | gold → +4 pts
 *   • ≥1 chauffeur formé sensRSE                              : +2 pts
 *
 * Score capé à 15.
 *
 * @returns {{ score: number, breakdown: object, level: string }}
 *   level = 'none' | 'standard' | 'modéré' | 'fort' (pour libellé UI)
 */
export function computeAoRseCommitmentScore(state) {
  const breakdown = {
    claire: 0,
    electricCharger: 0,
    electricFleet: 0,
    ecoVadis: 0,
    rseTraining: 0,
  };

  const hired = state.hiredSupport || {};
  if (hired['role-claire-marechal']) breakdown.claire = 5;

  const activeInv = state.activeInvestments || {};
  if (activeInv['inv-depot-11']) breakdown.electricCharger = 3;

  const vehicles = state.vehicles || [];
  const hasElectric = vehicles.some(v => {
    const specs = v.specialties || [];
    return specs.includes('ZFE') || specs.includes('Image verte');
  });
  if (hasElectric) breakdown.electricFleet = 3;

  const rseTier = getPlayerRseTier(state);
  if (rseTier === 'gold') breakdown.ecoVadis = 4;
  else if (rseTier === 'silver') breakdown.ecoVadis = 2;

  const drivers = state.drivers || [];
  const hasRseTraining = drivers.some(d => {
    const t = d.trainings || {};
    return t.sensRSE === true || t.sensRSE === 'completed';
  });
  if (hasRseTraining) breakdown.rseTraining = 2;

  const total = Math.min(15,
    breakdown.claire + breakdown.electricCharger + breakdown.electricFleet +
    breakdown.ecoVadis + breakdown.rseTraining);

  let level = 'none';
  if (total >= 11) level = 'fort';
  else if (total >= 6) level = 'modéré';
  else if (total >= 1) level = 'standard';

  return { score: total, breakdown, level };
}

/**
 * Returns the global eligibility envelope for the player. Used to
 * compute whether the AO page should be unlocked + which tenders to
 * surface, independently of any single tender.
 */
export function getTenderEligibility(state) {
  const commercialLevel = getPlayerCommercialLevel(state.hiredSupport || {});
  const hasClaire = !!(state.hiredSupport || {})['role-claire-marechal'];
  const fleetSize = (state.vehicles || []).length;
  const rseTier = getPlayerRseTier(state);
  const compliance = getCompanyHseCompliance(state.drivers || [], state.totalDays || 0);
  // Medical compliance % = (compliant drivers in 'medical' status) / total
  // applicable. If no drivers, treat as 100 % (nobody to fail).
  let medCompliant = 0, medApplicable = 0;
  for (const d of compliance.details) {
    const m = d.trainings.medical;
    if (m && m.applicable !== false) {
      medApplicable += 1;
      if (m.status === 'compliant') medCompliant += 1;
    }
  }
  const medicalPct = medApplicable === 0 ? 100 : Math.round((medCompliant / medApplicable) * 100);
  return {
    commercialLevel,            // null | 'junior' | 'senior' | 'expert'
    hasClaire,
    maxTier: getMaxAccessibleTier(commercialLevel),
    fleetSize,
    rseTier,
    medicalPct,
    hsePolicyOk: hasClaire,
    pageUnlocked: !!commercialLevel,
  };
}

/**
 * Filters the static TENDERS catalogue to the subset the player is
 * eligible to bid on.
 *
 * 04/06/2026 — Now also includes spot exclusivity tenders (`SPOT_TENDERS`)
 * applying the same eligibility rules. The player only sees one at a time
 * per spot client (filtered against `state.spotEngagements` active set).
 */
export function getAvailableTenders(state) {
  const elig = getTenderEligibility(state);
  if (!elig.pageUnlocked) return [];
  const maxRank = TENDER_TIER_RANK[elig.maxTier] || 0;
  const activeSpotTenderIds = new Set(
    (state.spotEngagements || []).filter(e => e.active).map(e => e.tenderId),
  );
  const all = [
    ...TENDERS,
    // Spot tenders excluded when the player already has an active engagement
    // with that client (no double exclusivity).
    ...SPOT_TENDERS.filter(t => !activeSpotTenderIds.has(t.id)),
    // 05/02/2026 — Convois exceptionnels via AO. Filtre éligibilité strict :
    // permis convoi + tracteur Heavyhaul XXL + phase >= 4.
    ...(isPlayerEligibleForExceptionalTender(state) ? EXCEPTIONAL_TENDERS : []),
  ];
  return all.filter(t => {
    if ((TENDER_TIER_RANK[t.tier] || 0) > maxRank) return false;
    if (elig.fleetSize < t.requiresFleet) return false;
    if ((RSE_TIER_RANK[elig.rseTier] || 0) < (RSE_TIER_RANK[t.requiresRseTier] || 0)) return false;
    if (elig.medicalPct < (t.requiresMedicalPct || 0)) return false;
    // National tier also requires Claire (HSE-heavy contracts).
    if (t.tier === 'national' && !elig.hasClaire) return false;
    return true;
  });
}

/**
 * Compute the win probability for a given offer on a given tender.
 * Pure function — easy to unit-test.
 *
 * 05/02/2026 (Q1 Lot 2) — `offer.hseCommitment` est désormais IGNORÉ.
 * Le bonus d'engagement RSE est calculé automatiquement depuis le state
 * via `computeAoRseCommitmentScore`. Le champ est conservé en signature
 * pour préserver la compatibilité avec les anciens tests / save games
 * mais n'influence plus le calcul.
 *
 * @param {Object} state          game state
 * @param {Object} tender         from TENDERS
 * @param {Object} offer
 * @param {number} offer.dailyPrice    asked € per truck-day
 * @param {number} offer.durationDays  proposed duration (must be in tender bracket)
 * @returns {{ probability: number, breakdown: object }}
 */
export function computeTenderWinProbability(state, tender, offer) {
  const elig = getTenderEligibility(state);
  const breakdown = {};
  let p = 0.50;

  // (1) Reputation + notoriety bundle.
  const repNot = ((state.reputation || 50) * 0.6 + (state.notoriety || 0) * 0.4) / 100;
  const repDelta = (repNot - 0.50) * 0.36; // ±0.18 max
  breakdown.reputation = +repDelta.toFixed(3);
  p += repDelta;

  // (2) Financial offer competitiveness.
  const askRatio = tender.baseDailyRevenue > 0 ? (offer.dailyPrice / tender.baseDailyRevenue) : 1;
  // Linear: 0.80 → +0.20, 1.00 → 0.00, 1.30 → -0.20
  const priceDelta = Math.max(-0.20, Math.min(0.20, (1 - askRatio) * 1.0));
  breakdown.price = +priceDelta.toFixed(3);
  p += priceDelta;

  // (3) HSE/RSE policy bonus from EcoVadis & global RSE score (legacy).
  const rseScoreVal = computeRseScore(state)?.overall || 0;
  const rseDelta = Math.max(0, ((rseScoreVal - 50) / 50)) * 0.15; // 50→0, 100→+0.15
  const tierBonus = (RSE_TIER_RANK[elig.rseTier] || 0) * 0.02; // +0.02 per tier above none
  breakdown.rse = +(rseDelta + tierBonus).toFixed(3);
  p += rseDelta + tierBonus;

  // (4) Fleet over-capacity.
  const fleetRatio = tender.requiresFleet > 0
    ? elig.fleetSize / tender.requiresFleet
    : 1;
  const fleetDelta = Math.min(0.08, Math.max(0, (fleetRatio - 1) * 0.04));
  breakdown.fleet = +fleetDelta.toFixed(3);
  p += fleetDelta;

  // (5) Financial reliability.
  const treasury = state.treasury || 0;
  const monthlyExp = state.monthlyExpenses || 1;
  const months = treasury / Math.max(1, monthlyExp); // months of runway
  // 1m → -0.10, 6m → 0, 12m → +0.10
  const finDelta = Math.max(-0.10, Math.min(0.10, ((months - 6) / 60)));
  breakdown.financial = +finDelta.toFixed(3);
  p += finDelta;

  // (6) Auto-RSE commitment score (replaces legacy manual HSE selector).
  //     score 0..15 → bonus 0.00 .. +0.15 (linear).
  const rseAuto = computeAoRseCommitmentScore(state);
  const commitDelta = +(rseAuto.score / 100).toFixed(3); // 15 → +0.15
  breakdown.hseCommitment = commitDelta;
  breakdown.rseAutoScore = rseAuto.score;
  breakdown.rseAutoBreakdown = rseAuto.breakdown;
  breakdown.rseAutoLevel = rseAuto.level;
  p += commitDelta;

  const probability = Math.max(0.05, Math.min(0.95, p));
  return { probability: +probability.toFixed(3), breakdown };
}

/**
 * 05/02/2026 (Q1 Lot 2) — Fourchette de probabilité de victoire.
 *
 * Le joueur ne voit plus un % fixe : il voit une **fourchette** (low–high)
 * qui se resserre autour de la probabilité réelle (mid) en fonction des
 * rôles support recrutés.
 *
 * Amplitude :
 *   • Sans aucun rôle support pertinent (commerce/marketing/RH) → ±0.20 (40 pts)
 *   • Chaque famille recrutée resserre la fourchette de ~0.057 (5,7 pts)
 *   • Avec les 3 familles → ±0.03 (6 pts)
 *
 * Familles considérées :
 *   • commerce  : role-lea-bertrand | role-marc-dupuis | role-sylvie-marchand
 *   • marketing : role-yanis-elkaroui | role-sherazade-fetitou
 *   • rh        : role-nadia-cheikh | role-karim-sefrioui
 *
 * @returns {{ low: number, mid: number, high: number, spread: number, breakdown: object, supportFamilies: string[] }}
 */
const FULL_SPREAD = 0.20; // ±20 pts
const NARROW_SPREAD = 0.03; // ±3 pts
const NARROWING_FAMILIES = ['commerce', 'marketing', 'rh'];

export function computeTenderProbabilityRange(state, tender, offer) {
  const { probability, breakdown } = computeTenderWinProbability(state, tender, offer);
  const hired = state.hiredSupport || {};
  const hasCommerce = !!(hired['role-lea-bertrand'] || hired['role-marc-dupuis'] || hired['role-sylvie-marchand']);
  const hasMarketing = !!(hired['role-yanis-elkaroui'] || hired['role-sherazade-fetitou']);
  const hasRh = !!(hired['role-nadia-cheikh'] || hired['role-karim-sefrioui']);
  const supportFamilies = [];
  if (hasCommerce) supportFamilies.push('commerce');
  if (hasMarketing) supportFamilies.push('marketing');
  if (hasRh) supportFamilies.push('rh');

  // Linear narrowing : 3 familles couvrent la totalité de l'écart
  // (FULL_SPREAD - NARROW_SPREAD).
  const step = (FULL_SPREAD - NARROW_SPREAD) / NARROWING_FAMILIES.length;
  const spread = +(FULL_SPREAD - supportFamilies.length * step).toFixed(3);

  const low = +Math.max(0.05, probability - spread).toFixed(3);
  const high = +Math.min(0.95, probability + spread).toFixed(3);

  return {
    low,
    mid: probability,
    high,
    spread,
    breakdown,
    supportFamilies,
  };
}

/**
 * Roll the tender outcome given a probability.
 * Returns 'won' | 'counter' | 'lost'.
 *
 *   - probability >= 0.70 : either 'won' (70 %) or 'counter' (30 %)
 *     (the client likes you and will sign or negotiate a tighter price)
 *   - 0.40 <= probability < 0.70 : 'counter' (60 %) or 'lost' (40 %)
 *   - probability < 0.40 : 'lost' (90 %) or 'counter' (10 %)
 *
 * Counter-offers always shave 8 % off the player's submitted price.
 *
 * 12/02/2026 — Rétro-effet "presse" : quand un seuil marketScale vient
 * d'être franchi, les concurrents sont temporairement plus agressifs.
 * Les rangs `won` s'affaissent au profit de `counter` (moins de
 * signatures immédiates, plus de négociations tirées vers le bas).
 *
 * @param {number} probability - 0..1
 * @param {Function} [rng] - random source (test hook)
 * @param {boolean} [aggressive] - active la fenêtre agressive
 */
export function rollTenderOutcome(probability, rng = Math.random, aggressive = false) {
  const r = rng();
  if (probability >= 0.70) {
    // Standard : 70 % won / 30 % counter. Agressif : 55 % won / 45 % counter.
    const wonThreshold = aggressive ? 0.55 : 0.70;
    return r < wonThreshold ? 'won' : 'counter';
  }
  if (probability >= 0.40) {
    // Standard : 60 % counter / 40 % lost. Agressif : 75 % counter / 25 % lost.
    const counterThreshold = aggressive ? 0.75 : 0.60;
    return r < counterThreshold ? 'counter' : 'lost';
  }
  // Standard : 10 % counter / 90 % lost. Agressif : 25 % counter / 75 % lost
  // (les concurrents descendent plus bas dans le classement pour vous
  // rattraper au moindre créneau).
  const counterThreshold = aggressive ? 0.25 : 0.10;
  return r < counterThreshold ? 'counter' : 'lost';
}

/**
 * 04/06/2026 — Tender-won bonus multiplier.
 *
 * Tenders are rarer, more demanding (RSE, fleet size, training quality)
 * and more difficult to win than classical contracts. The player asked
 * for a meaningful financial reward on top of the negotiated price.
 *
 * We apply a flat +30 % on the daily price at contract issuance. The UI
 * surfaces the bonus separately so the player understands the upside.
 *
 * National tier (the toughest) gets +35 % to keep the curve steep.
 */
export const TENDER_BONUS_PCT = 0.30;
export const TENDER_BONUS_PCT_NATIONAL = 0.35;

/**
 * 12/02/2026 — Bonus dynamique selon la progression du joueur.
 * Retour user prod : « Les AO doivent rester plus attractifs que les
 * contrats standards en mid-late game ». On additionne `aoBonusBoost`
 * (issu de `computeMarketScale`) au bonus fixe, plafonné à +65 % (35 %
 * national + 30 % boost max) pour rester pédagogiquement lisible.
 */
export function getTenderBonusPct(tender, state = null) {
  const base = tender.tier === 'national' ? TENDER_BONUS_PCT_NATIONAL : TENDER_BONUS_PCT;
  if (!state) return base;
  // Lazy require pour éviter tout risque de dépendance circulaire.
  const { marketScaleFromState } = require('../../utils/marketScale');
  const { aoBonusBoost } = marketScaleFromState(state);
  return Math.min(0.65, base + aoBonusBoost);
}

/**
 * Convert a tender + accepted offer into a contract template ready to land
 * in `availableContracts`. Caller is responsible for adding a fresh
 * instanceId before pushing to state.
 *
 * 12/02/2026 — Accepte facultativement `state` pour le scaling AO
 * dynamique (bonus revalorisé en mid/late game).
 */
export function buildContractFromTender(tender, acceptedOffer, state = null) {
  const bonusPct = getTenderBonusPct(tender, state);
  const negotiatedPrice = acceptedOffer.dailyPrice || 0;
  const dailyRevenue = Math.round(negotiatedPrice * (1 + bonusPct));
  return {
    id: `${tender.id}-contract`,
    name: `${tender.clientName} — Marché ${tender.tier}`,
    client: tender.clientName,
    category: tender.sector,
    frequency: 'régulier',
    duration: acceptedOffer.durationDays,
    dailyRevenue,
    // Surface the breakdown so UI can show "prix négocié + prime AO".
    dailyRevenueBase: negotiatedPrice,
    dailyRevenueBonus: dailyRevenue - negotiatedPrice,
    dailyRevenueBonusPct: bonusPct,
    tier: 1, // contract tier (vehicle gate), not tender tier
    tierMin: 1,
    tenderWon: true,                  // marker for UI badge
    tenderId: tender.id,
    tenderTier: tender.tier,
    requiresRseTier: tender.requiresRseTier,
    // 05/02/2026 — Q1 retour user prod : propage le kind pour le routing
    // au staffing (convoi exceptionnel → startConvoyExceptionnel).
    kind: tender.kind || 'standard',
    convoyArchetypeId: tender.archetypeId || null,
    cargo: tender.cargo || null,
  };
}

/**
 * Competitive intelligence — Marc Dupuis / Sylvie Marchand can debrief the
 * competition before the player submits. Léa Bertrand alone returns a
 * "limited" disclaimer.
 *
 * @param {Object} state — game state (reads hiredSupport)
 * @param {Object} tender — from TENDERS
 * @returns {{ unlocked: boolean, signerLabel: string, competitors: string[],
 *             priceRange: [number, number]|null, advice: string }}
 */
export function getCompetitiveIntel(state, tender) {
  const hired = state.hiredSupport || {};
  const hasSylvie = !!hired['role-sylvie-marchand'];
  const hasMarc = !!hired['role-marc-dupuis'];
  const hasLea = !!hired['role-lea-bertrand'];
  const competitors = Array.isArray(tender.competitors) ? tender.competitors : [];
  const priceRange = Array.isArray(tender.competitorPriceRange) ? tender.competitorPriceRange : null;

  // Sylvie : full intel (names + price range + qualitative advice).
  if (hasSylvie) {
    const advice = priceRange
      ? `D'après mon réseau et les marchés similaires récents, mes contacts proposeront entre **${priceRange[0]} et ${priceRange[1]} €/j**. Pour gagner ce dossier ${tender.tier === 'national' ? 'national' : 'régional'}, soyez nettement sous le bas de la fourchette OU compensez avec un engagement HSE fort.`
      : `Je n'ai pas encore d'intel précise sur les positions concurrents — laissez-moi 48 h pour les sonder.`;
    return {
      unlocked: true,
      signerLabel: 'Sylvie Marchand — Directrice commerciale',
      competitors,
      priceRange,
      advice,
    };
  }

  // Marc : competitor names + indicative range, lighter qualitative advice.
  if (hasMarc) {
    const advice = priceRange
      ? `J'estime les concurrents entre **${priceRange[0]} et ${priceRange[1]} €/j** sur ce profil. Méfiez-vous des positions trop hautes — sur ce tier, l'acheteur compare ${competitors.length} prestataires en moyenne.`
      : `Concurrents identifiés, mais je n'ai pas d'estimation prix consolidée pour le moment.`;
    return {
      unlocked: true,
      signerLabel: 'Marc Dupuis — Commercial terrain',
      competitors,
      priceRange,
      advice,
    };
  }

  // Léa : limited intel — names visible but no price intelligence.
  if (hasLea) {
    return {
      unlocked: false,
      signerLabel: 'Léa Bertrand — Commerciale junior',
      competitors,
      priceRange: null,
      advice:
        "Je n'ai pas encore le carnet d'adresses suffisant pour vous donner une estimation des positions concurrents. Investir dans un commercial **senior** (Marc Dupuis) débloquera cette intelligence.",
    };
  }

  return { unlocked: false, signerLabel: null, competitors: [], priceRange: null, advice: '' };
}
